import type { Card } from '../types'

/**
 * Parse a raw decklist text into mainboard + sideboard card lists.
 *
 * - One card per line: `4 Lightning Bolt`, `2 Counterspell`, or just a name
 *   (quantity defaults to 1). Also tolerates MTGA-ish `4 Name (SET) 123`.
 * - A line containing only "sideboard" or "banquillo" (case-insensitive)
 *   switches the following lines into the sideboard section.
 * - Blank lines and comment lines (starting with `//` or `#`) are ignored.
 *
 * This runs entirely in the frontend (no backend call) — we never resolve
 * images here (Cristian's decision: text-only lists).
 */
export interface ParsedDecklist {
  mainboard: Card[]
  sideboard: Card[]
  /** Lines that could not be parsed (reported to the user). */
  unparsed: string[]
}

const SIDEBAR_RE = /^\s*(sideboard|banquillo)\s*:?\s*$/i
const COMMENT_RE = /^\s*(\/\/|#)/
// MTGA: "4 Lightning Bolt (M11) 146" → qty, name, set, collector
const MTGA_RE = /^(\d+)\s+(.+?)\s*(?:\(([A-Za-z0-9]+)\)\s*([A-Za-z0-9][A-Za-z0-9-]*)?)?(?:\s+[*✶][A-Za-z][*✶])?$/
// TappedOut / XMage style: "1x Lightning Bolt" or "4x Counterspell"
const NX_RE = /^(\d+)\s*x\s+(.+)$/i
// Section headers used by TappedOut exports ("About", "Commander", "Deck",
// "Maybeboard", "Acquire", ...). They are NOT cards — only "sideboard"/
// "banquillo" switch zone; the rest are ignored so they don't become 1x cards.
const SECTION_HEADER_RE =
  /^\s*(about|commander|commanders|deck|mainboard|main\s*deck|maybeboard|acquire|companion|planes|schemes|tokens?)\s*:?\s*$/i

/** Minimal CSV line splitter that respects double-quoted fields ("" = escaped "). */
function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      out.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out.map((s) => s.trim())
}

/**
 * Detect a CSV export (Moxfield / TappedOut / Scryfall) and return its rows as
 * cards, or null if the text is not CSV. These formats carry set + collector
 * number, so the chosen art/printing is preserved.
 *
 * Recognised headers (case-insensitive):
 * - Moxfield/Scryfall: count,name,set_code/collector_number (or set/cn, scryfall_id)
 * - TappedOut:         Qty,Name,Printing(,Board)
 */
function parseCsv(text: string): { mainboard: Card[]; sideboard: Card[] } | null {
  const lines = text.split('\n').filter((l) => l.trim())
  if (lines.length < 2) return null
  const header = splitCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/^\uFEFF/, ''))
  const col = (...names: string[]) => names.map((n) => header.indexOf(n)).find((i) => i >= 0) ?? -1

  const qtyIdx = col('count', 'qty', 'quantity')
  const nameIdx = col('name')
  const setIdx = col('set_code', 'set', 'printing', 'edition')
  const cnIdx = col('collector_number', 'cn', 'collector number')
  const boardIdx = col('board', 'section')
  if (nameIdx < 0 || qtyIdx < 0) return null // not a recognisable deck CSV

  const mainboard: Card[] = []
  const sideboard: Card[] = []
  for (const line of lines.slice(1)) {
    const cells = splitCsvLine(line)
    const name = cells[nameIdx]
    const qty = parseInt(cells[qtyIdx], 10)
    if (!name || Number.isNaN(qty)) continue
    const card: Card = {
      name,
      quantity: qty,
      set_code: setIdx >= 0 && cells[setIdx] ? cells[setIdx].toLowerCase() : null,
      collector_number: cnIdx >= 0 && cells[cnIdx] ? cells[cnIdx] : null,
    }
    const board = boardIdx >= 0 ? cells[boardIdx].toLowerCase() : ''
    if (board === 'side' || board === 'sideboard') sideboard.push(card)
    else mainboard.push(card)
  }
  return mainboard.length + sideboard.length > 0 ? { mainboard, sideboard } : null
}

function parseLine(line: string): Card | null {
  const trimmed = line.trim()
  if (!trimmed) return null

  const mtga = MTGA_RE.exec(trimmed)
  if (mtga) {
    const [, qty, name, setCode, collector] = mtga
    return {
      name: name.trim(),
      quantity: parseInt(qty, 10),
      set_code: setCode ?? null,
      collector_number: collector ?? null,
    }
  }

  // TappedOut / XMage: "1x Lightning Bolt" (qty + "x" + name).
  const nx = NX_RE.exec(trimmed)
  if (nx) {
    return { name: nx[2].trim(), quantity: parseInt(nx[1], 10) }
  }

  // Starts with a digit but didn't match MTGA — salvage leading number.
  if (/^\d/.test(trimmed)) {
    const parts = trimmed.split(/\s+/)
    const qty = parseInt(parts[0], 10)
    if (!Number.isNaN(qty) && parts.length > 1) {
      return { name: parts.slice(1).join(' '), quantity: qty }
    }
    return null
  }

  // Plain "Name" with no leading number → quantity 1.
  return { name: trimmed, quantity: 1 }
}

export function parseDecklist(text: string): ParsedDecklist {
  // CSV exports (Moxfield/TappedOut/Scryfall) carry set + collector number, so
  // they preserve the chosen art. Try CSV first; fall back to line parsing.
  const csv = parseCsv(text)
  if (csv) return { ...csv, unparsed: [] }

  const mainboard: Card[] = []
  const sideboard: Card[] = []
  const unparsed: string[] = []
  let inSideboard = false

  // If the text has TappedOut-style section headers (About/Deck/Commander/...),
  // it's a structured export: only lines that start with a quantity ("4 Name"
  // or "4x Name") are cards — free-text lines (the About description, etc.) are
  // ignored. In a plain list (no section headers) keep accepting bare names.
  const hasSections = text
    .split('\n')
    .some((l) => SECTION_HEADER_RE.test(l.trim()) || SIDEBAR_RE.test(l.trim()))

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim()
    if (!line || COMMENT_RE.test(line)) continue

    if (SIDEBAR_RE.test(line)) {
      inSideboard = true
      continue
    }

    // TappedOut-style section headers ("About", "Commander", "Deck", ...) are
    // not cards — skip them so they don't become 1x entries. Only
    // "sideboard"/"banquillo" (handled above) switch the zone.
    if (SECTION_HEADER_RE.test(line)) continue

    // Structured export: skip any line that doesn't start with a quantity.
    if (hasSections && !/^\d/.test(line)) continue

    const card = parseLine(line)
    if (!card) {
      unparsed.push(rawLine)
      continue
    }
    if (inSideboard) {
      sideboard.push(card)
    } else {
      mainboard.push(card)
    }
  }

  return { mainboard, sideboard, unparsed }
}

/** Serialize a list back to decklist text (for re-editing / re-parsing). */
export function decklistToText(mainboard: Card[], sideboard: Card[]): string {
  // Preserve set + collector number so re-pasting keeps the chosen art.
  const toLine = (c: Card) => {
    let line = `${c.quantity} ${c.name}`
    if (c.set_code) {
      line += ` (${c.set_code.toUpperCase()})`
      if (c.collector_number) line += ` ${c.collector_number}`
    }
    return line
  }
  const lines = mainboard.map(toLine)
  if (sideboard.length > 0) {
    lines.push('sideboard')
    for (const c of sideboard) lines.push(toLine(c))
  }
  return lines.join('\n')
}
